import React, { useState, useEffect } from 'react';
import { Building2, Users, Truck, Search, ChevronRight, RefreshCw } from 'lucide-react';
import { api } from '../../utils/api';

const ClientsPanel = ({ onViewClient }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    setLoading(true);
    try {
      const data = await api('/sysadmin/clients');
      setClients(data.clients || []);
    } catch (err) {
      console.error('Failed to load clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = clients.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.abn && c.abn.includes(search))
  );

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div data-testid="sysadmin-clients-panel">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-lg font-semibold text-white">All Clients</h2>
          <p className="text-sm text-slate-400">{clients.length} businesses registered</p>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-initial">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search clients..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full sm:w-64 pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500"
              data-testid="clients-search"
            />
          </div>
          <button onClick={loadClients} className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition" data-testid="clients-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full" />
        </div>
      ) : (
        <div className="bg-slate-800/50 rounded-2xl border border-slate-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left p-4 text-slate-400 font-medium">Client Name</th>
                  <th className="text-left p-4 text-slate-400 font-medium hidden sm:table-cell">Status</th>
                  <th className="text-center p-4 text-slate-400 font-medium">Drivers</th>
                  <th className="text-center p-4 text-slate-400 font-medium hidden md:table-cell">Vehicles</th>
                  <th className="text-center p-4 text-slate-400 font-medium hidden md:table-cell">Users</th>
                  <th className="text-left p-4 text-slate-400 font-medium hidden lg:table-cell">Created</th>
                  <th className="text-center p-4 text-slate-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.client_id} className="border-b border-slate-700/50 hover:bg-slate-700/20 transition" data-testid={`client-row-${c.client_id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-blue-500/15 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-4 h-4 text-blue-400" />
                        </div>
                        <div>
                          <div className="text-white font-medium">{c.name}</div>
                          {c.abn && <div className="text-xs text-slate-500">ABN: {c.abn}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="p-4 hidden sm:table-cell">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        c.status === 'ACTIVE' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/20 text-slate-400'
                      }`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <Users className="w-3.5 h-3.5 text-emerald-400" />
                        <span className="text-white">{c.driver_count}</span>
                      </div>
                    </td>
                    <td className="p-4 text-center hidden md:table-cell">
                      <div className="flex items-center justify-center gap-1.5">
                        <Truck className="w-3.5 h-3.5 text-amber-400" />
                        <span className="text-white">{c.vehicle_count}</span>
                      </div>
                    </td>
                    <td className="p-4 text-center hidden md:table-cell">
                      <span className="text-white">{c.user_count}</span>
                    </td>
                    <td className="p-4 hidden lg:table-cell text-slate-400 text-xs">
                      {formatDate(c.created_at)}
                    </td>
                    <td className="p-4 text-center">
                      <button
                        onClick={() => onViewClient(c)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/15 hover:bg-blue-500/30 text-blue-400 text-xs font-medium rounded-lg transition"
                        data-testid={`view-client-${c.client_id}`}
                      >
                        View <ChevronRight className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-slate-500">
                      {search ? 'No clients matching your search' : 'No clients found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientsPanel;
